import i18n from "../../i18n";
import {
  TASK_STATUS_LABELS as TASK_STATUS_LABELS_FALLBACK,
  TASK_TYPE_LABELS as TASK_TYPE_LABELS_FALLBACK,
  taskStatusColor,
} from "./skillFichaHelpers";

function tt(key, fallback, vars) {
  const translated = i18n.t(key, vars);
  if (translated === key) return fallback;
  return translated;
}

export function taskClientLabel(row) {
  return (
    row?.CustomerName ||
    (row?.CustomerId
      ? tt("skillAgent.tree.clientNumber", `Cliente #${row.CustomerId}`, {
          id: row.CustomerId,
        })
      : null) ||
    row?.SkillSlug ||
    tt("skillAgent.tree.noClient", "Sin cliente")
  );
}

export function taskSeasonLabel(row) {
  const season = String(row?.Temporada || "").trim();
  return season || tt("skillAgent.tree.noSeason", "Sin temporada");
}

export function searchStatusLabel(status) {
  const key = `skillAgent.searchStatus.${status}`;
  const mapFallback = {
    draft: "Borrador",
    running: "Buscando…",
    done: "Listo",
    error: "Error",
    cancelled: "Cancelado",
  };
  if (status && i18n.exists(key)) return i18n.t(key);
  return mapFallback[status] || status || tt("skillAgent.common.emDash", "—");
}

export function moodboardStatusLabel(status) {
  const key = `skillAgent.moodboardStatus.${status}`;
  const mapFallback = {
    draft: "Borrador",
    ready: "PDF listo",
  };
  if (status && i18n.exists(key)) return i18n.t(key);
  return mapFallback[status] || status || tt("skillAgent.common.emDash", "—");
}

export function taskStatusLabel(status) {
  const key = `skillAgent.taskStatus.${status}`;
  if (status && i18n.exists(key)) return i18n.t(key);
  return TASK_STATUS_LABELS_FALLBACK[status] || status || "—";
}

export function taskTypeLabel(type) {
  const key = `skillAgent.taskType.${type}`;
  if (type && i18n.exists(key)) return i18n.t(key);
  return TASK_TYPE_LABELS_FALLBACK[type] || type || "—";
}

/** @deprecated prefer taskStatusLabel(); kept for callers that import the map */
export const TASK_STATUS_LABELS = TASK_STATUS_LABELS_FALLBACK;
/** @deprecated prefer taskTypeLabel() */
export const TASK_TYPE_LABELS = TASK_TYPE_LABELS_FALLBACK;

export { taskStatusColor };

/**
 * groupBy: "season" → Temporada → Cliente → DISEÑOS + SEARCHS + MOODBOARDS
 * groupBy: "client" → Cliente → Temporada → DISEÑOS + SEARCHS + MOODBOARDS
 */
export function buildAgentTasksTree(
  rows = [],
  groupBy = "season",
  searches = [],
  moodboards = [],
) {
  const filtered = Array.isArray(rows) ? rows : [];
  const searchRows = Array.isArray(searches) ? searches : [];
  const moodboardRows = Array.isArray(moodboards) ? moodboards : [];
  const rootMap = new Map();
  const noSeason = tt("skillAgent.tree.noSeason", "Sin temporada");
  const noClient = tt("skillAgent.tree.noClient", "Sin cliente");
  const folderDesigns = tt("skillAgent.tree.folderDesigns", "DISEÑOS");
  const folderSearchs = tt("skillAgent.tree.folderSearchs", "SEARCHS");
  const folderMoodboards = tt("skillAgent.tree.folderMoodboards", "MOODBOARDS");

  function ensureL2(L1, L2, kinds) {
    if (!rootMap.has(L1)) {
      rootMap.set(L1, {
        id: `l1:${L1}`,
        label: L1,
        kind: kinds.l1,
        children: new Map(),
        tasks: [],
      });
    }
    const n1 = rootMap.get(L1);
    if (!n1.children.has(L2)) {
      n1.children.set(L2, {
        id: `l2:${L1}::${L2}`,
        label: L2,
        kind: kinds.l2,
        tasks: [],
        searches: [],
        moodboards: [],
      });
    }
    return n1.children.get(L2);
  }

  const kinds =
    groupBy === "client"
      ? { l1: "client", l2: "season" }
      : { l1: "season", l2: "client" };

  for (const row of filtered) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).tasks.push(row);
  }

  for (const row of searchRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).searches.push(row);
  }

  for (const row of moodboardRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).moodboards.push(row);
  }

  const sortLabel = (a, b) =>
    String(a).localeCompare(String(b), i18n.language || "es", {
      numeric: true,
      sensitivity: "base",
    });

  const roots = [...rootMap.values()]
    .map((n1) => {
      const children = [...n1.children.values()]
        .map((n2) => {
          const tasks = [...n2.tasks].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const searchesSorted = [...(n2.searches || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const moodboardsSorted = [...(n2.moodboards || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const ctxCustomerId =
            tasks.find((t) => t.CustomerId)?.CustomerId ||
            searchesSorted.find((s) => s.CustomerId)?.CustomerId ||
            moodboardsSorted.find((m) => m.CustomerId)?.CustomerId ||
            null;
          const Temporada = groupBy === "client" ? n2.label : n1.label;
          const CustomerName = groupBy === "client" ? n1.label : n2.label;
          const branchCtx = {
            seasonLabel: Temporada,
            clientLabel: CustomerName,
            Temporada,
            CustomerName,
            CustomerId: ctxCustomerId,
          };
          const disenosNode = {
            id: `${n2.id}::disenos`,
            label: folderDesigns,
            kind: "disenos",
            tasks,
            searches: [],
            moodboards: [],
            count: tasks.length,
            ...branchCtx,
          };
          const searchsNode = {
            id: `${n2.id}::searchs`,
            label: folderSearchs,
            kind: "searchs",
            searches: searchesSorted,
            tasks: [],
            count: searchesSorted.length,
            ...branchCtx,
          };
          const moodboardsNode = {
            id: `${n2.id}::moodboards`,
            label: folderMoodboards,
            kind: "moodboards",
            moodboards: moodboardsSorted,
            tasks: [],
            count: moodboardsSorted.length,
            ...branchCtx,
          };
          return {
            ...n2,
            tasks: [],
            searches: searchesSorted,
            moodboards: moodboardsSorted,
            disenosNode,
            searchsNode,
            moodboardsNode,
            count:
              tasks.length + searchesSorted.length + moodboardsSorted.length,
          };
        })
        .sort((a, b) => sortLabel(a.label, b.label));
      const count = children.reduce((s, c) => s + c.count, 0);
      return { ...n1, children, count };
    })
    .sort((a, b) => {
      if (a.label === noSeason || a.label === noClient) return 1;
      if (b.label === noSeason || b.label === noClient) return -1;
      return sortLabel(a.label, b.label);
    });

  return roots;
}

/**
 * IDs de nodos a expandir para revelar un moodboard / search / task en el árbol.
 */
export function treeExpandIdsForSelection(
  tree,
  { moodboardId, searchId, taskId } = {},
) {
  const mb =
    moodboardId != null && String(moodboardId) !== ""
      ? Number(moodboardId)
      : null;
  const se =
    searchId != null && String(searchId) !== "" ? Number(searchId) : null;
  const tk = taskId != null && String(taskId) !== "" ? Number(taskId) : null;
  if (
    (mb == null || !Number.isFinite(mb)) &&
    (se == null || !Number.isFinite(se)) &&
    (tk == null || !Number.isFinite(tk))
  ) {
    return [];
  }
  for (const n1 of tree || []) {
    for (const n2 of n1.children || []) {
      if (
        mb != null &&
        (n2.moodboards || []).some((m) => Number(m.id) === mb)
      ) {
        return [n1.id, n2.id, n2.moodboardsNode?.id].filter(Boolean);
      }
      if (se != null && (n2.searches || []).some((s) => Number(s.id) === se)) {
        return [n1.id, n2.id, n2.searchsNode?.id].filter(Boolean);
      }
      if (
        tk != null &&
        (n2.disenosNode?.tasks || n2.tasks || []).some(
          (t) => Number(t.id) === tk,
        )
      ) {
        return [n1.id, n2.id, n2.disenosNode?.id].filter(Boolean);
      }
    }
  }
  return [];
}
